import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import config from '../../config';
import { getLocalStorageItem, removeLocalStorageItem } from '../../utils/storage';
import { Api, ApiConfig } from '../api';
import { supabase } from './supabase';

// 响应类型定义
export interface BetaStatusResponse {
    betaAccess: boolean;
}

export interface RedeemCodeResponse {
    success: boolean;
    message?: string;
}

export interface ShopBinding {
    id: string;
    shopId: string;
    domain: string;
}

export interface ShopInfo {
    id: string;
    domain: string;
    brandName: string;
    platform: string;
    timezone: string;
    physicFlag: number;
}

export interface ShopifyConnectRequest {
    shop_domain: string;
    redirect_url: string;
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface ShopifyConnectResponse {
    shop: string;
    authorizationUrl: string;
}

export interface ShopifyDisconnectRequest {
    shop_domain: string;
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface ShopifyDisconnectResponse {
    success: boolean;
    message?: string;
}

export interface UserInfo {
    id: string;
    email: string;
    userName: string;
}

export interface Workspace {
    id: string;
    name: string;
    logoUrl: string | null;
    role: string;
}

export interface WorkspacePlan {
    planId: string;
    planName: string;
    planCode: string;
    tier: string;
    status: string;
    startAt: string;
    trialEndAt: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    currency: string;
    billingCycle: string;
    provider: string;
}

export interface VerifyAuthResponse {
    userInfo: UserInfo;
    workspace: Workspace | null;
    workspacePlan: WorkspacePlan | null;
}

export interface NangoConnectTokenRequest {
    integration_id: string;
    [key: string]: any;
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface NangoConnectTokenResponse {
    data?: { token?: string };
    result?: { token?: string };
    token?: string;
    action?: string;
    url?: string;
    connectionId?: string;
}

export interface NangoUnbindRequest {
    integration_id: string;
    axiosRequestConfig?: AxiosRequestConfig;
}

// ============ Aliexpress Integration API Types ============

export interface AliexpressAuthorizeRequest {
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface AliexpressAuthorizeData {
    auth_url: string;
    redirect_uri: string;
}

export interface AliexpressAuthorizeResponse {
    code: string;
    message: string;
    status: string;
    data: AliexpressAuthorizeData;
}

export interface AliexpressUnbindRequest {
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface AliexpressUnbindData {
    workspace_id: string;
    status: string;
}

export interface AliexpressUnbindResponse {
    code: string;
    message: string;
    status: string;
    data: AliexpressUnbindData;
}

// ============ Workspace Integrations API Types ============

// Workspace Integration Mapping types (from API response)
export interface WorkspaceIntegrationMapping {
    last_synced_at: string | null;
    role?: string;
    updated_at: string;
    user_id: string;
    connection_id: string;
    account_name?: string | null;
    created_at: string;
    status?: string;
    customer_id?: string;
    time_zone?: string | null;
    currency_code?: string | null;
    account_id?: string;
    provider?: string;
    provider_config_key?: string;
    environment?: string;
    direct?: boolean;
    mapping_status?: string;
    account_status?: string;
}

// Workspace Integration type (from API response)
export interface WorkspaceIntegrationAPI {
    connected: boolean;
    environment: string;
    mappings: WorkspaceIntegrationMapping[];
    updated_at: string;
    provider: string;
    created_at: string;
    profile_data: any;
}

// Response format for workspace integrations API
export interface WorkspaceIntegrationsAPIResponse {
    [provider: string]: WorkspaceIntegrationAPI[];
}

export interface GetWorkspaceIntegrationsResponse {
    code: string;
    message: string;
    status: string;
    data: WorkspaceIntegrationsAPIResponse;
}

// OAuth 上下文类型
export interface OAuthContext {
    client_id: string;
    redirect_uri: string | null;
    device_id: string | null;
    login_trace_id: string | null;
}

// OAuth 授权响应类型
export interface OAuthAuthorizeResponse {
    code?: string;
    [key: string]: any;
}

export class AuthService {
    private api: Api;

    constructor(config: ApiConfig) {
        // 统一使用传入的 baseURL
        this.api = new Api({
            ...config
        });
    }

    /**
     * 验证用户身份
     */
    public async verifyAuth(): Promise<VerifyAuthResponse> {
        try {
            const response = await this.api.get<VerifyAuthResponse>('/api/auth/verify');
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * 获取用户的 beta 状态
     */
    public async getBetaStatus(): Promise<BetaStatusResponse> {
        try {
            const response = await this.api.get<BetaStatusResponse>('/api/beta/status');
            return response;
        } catch (error) {
            return { betaAccess: true };
        }
    }

    /**
     * 兑换邀请码
     * @param code 邀请码
     */
    public async redeemInviteCode(code: string): Promise<RedeemCodeResponse> {
        try {
            const response = await this.api.post<RedeemCodeResponse>('/api/beta/redeem', { code });
            return { success: true, message: response.message };
        } catch (error) {
            return { success: false, message: 'Verify code failed' };
        }
    }

    /**
     * 绑定 Shopify 店铺
     * @param accessToken Shopify Admin API access token
     * @param domain Shopify 店铺域名
     */
    public async bindShop({ accessToken, domain }: { accessToken: string, domain: string }): Promise<string> {
        try {
            const response = await this.api.post<string>('/api/shop-binding/bind', { accessToken, domain });
            return response;
        } catch (error) {
            console.error('Bind shop failed:', error);
            return 'fail';
        }
    }

    /**
     * Connect to Shopify after Google login
     * @param shopDomain Shopify shop domain
     * @param redirectUrl Redirect URL after connection
     * @param authorization Authorization token
     */
    public async connectShopify(
        {
            shop_domain, redirect_url, axiosRequestConfig 
        }: ShopifyConnectRequest
    ): Promise<ShopifyConnectResponse> {
        try {
            const response = await this.api.post<ShopifyConnectResponse>(
                '/api/shopify/connect',
                {
                    shop_domain: shop_domain,
                    redirect_url: redirect_url,
                    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Connect Shopify failed:', error);
            throw error;
        }
    }

    /**
     * Disconnect from Shopify store
     * @param data Shopify disconnect data
     * @returns Promise with disconnect result
     */
    public async disconnectShopify(
        { shop_domain, axiosRequestConfig }: ShopifyDisconnectRequest
    ): Promise<ShopifyDisconnectResponse> {
        try {
            const response = await this.api.post<ShopifyDisconnectResponse>(
                `/api/shopify/disconnect?shop_domain=${shop_domain}`,
                undefined,
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Disconnect Shopify failed:', error);
            throw error;
        }
    }

    /**
     * 获取 Google Ads 账户列表
     */
    public async getNangoGoogleAdsAccounts(): Promise<any> {
        try {
            const response = await this.api.get<any>('/api/nango/user/google-ads/accounts');
            return response;
        } catch (error) {
            console.error('Get Google Ads accounts failed:', error);
            throw error;
        }
    }

    /**
     * 获取 Nango 连接 Token
     */
    public async getNangoConnectToken(data: NangoConnectTokenRequest): Promise<NangoConnectTokenResponse> {
        try {
            const { axiosRequestConfig, ...requestData } = data;
            const response = await this.api.post<NangoConnectTokenResponse>(
                '/api/nango/connect-token',
                requestData,
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Get Nango connect token failed:', error);
            throw error;
        }
    }

    /**
     * 解绑 Nango 集成
     */
    public async unbindNangoIntegration(data: NangoUnbindRequest): Promise<void> {
        try {
            const { axiosRequestConfig, ...requestData } = data;
            await this.api.post('/api/nango/unbind', requestData, axiosRequestConfig);
        } catch (error) {
            console.error('Unbind Nango integration failed:', error);
            throw error;
        }
    }

    /**
     * Aliexpress 授权 - 获取授权 URL
     * API 会自动提取 response.data.data，返回的是 AliexpressAuthorizeData
     */
    public async authorizeAliexpress(
        { axiosRequestConfig }: AliexpressAuthorizeRequest
    ): Promise<AliexpressAuthorizeData> {
        try {
            const response = await this.api.get<AliexpressAuthorizeData>(
                '/api/aliexpress/authorize',
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Aliexpress authorize failed:', error);
            throw error;
        }
    }

    /**
     * Aliexpress 解绑
     * API 会自动提取 response.data.data，返回的是 AliexpressUnbindData
     */
    public async unbindAliexpress(
        { axiosRequestConfig }: AliexpressUnbindRequest
    ): Promise<AliexpressUnbindData> {
        try {
            const response = await this.api.post<AliexpressUnbindData>(
                '/api/aliexpress/unbind',
                undefined,
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Aliexpress unbind failed:', error);
            throw error;
        }
    }

    /**
     * 获取工作区下的所有集成
     * @param axiosRequestConfig - Optional axios config (must include X-Workspace-Id header)
     * @returns Promise with workspace integrations data
     */
    public async getWorkspaceIntegrations(
        axiosRequestConfig?: AxiosRequestConfig
    ): Promise<GetWorkspaceIntegrationsResponse> {
        try {
            const response = await this.api.get<GetWorkspaceIntegrationsResponse>(
                '/api/workspaces/integrations',
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Get workspace integrations failed:', error);
            throw error;
        }
    }

    /**
     * 获取 OAuth 授权码
     * 从 localStorage 中读取 oauth_context 参数并请求授权
     * 使用 AJAX 请求但不跟随重定向，手动提取 location 头并跳转
     */
    public async getOAuthAuthorize(workspaceId?: string | null): Promise<void> {
        console.log('[getOAuthAuthorize] 开始执行，检查 localStorage 中的 oauth_context');

        try {
            // 从 localStorage 获取 oauth_context
            const oauthContextStr = getLocalStorageItem('bustly_oauth_context');
            console.log('[getOAuthAuthorize] oauth_context 原始值:', oauthContextStr);

            if (!oauthContextStr) {
                console.warn('[getOAuthAuthorize] ⚠️ localStorage 中不存在 oauth_context，跳过 OAuth 授权请求');
                return;
            }

            const oauthContext: OAuthContext = JSON.parse(oauthContextStr);
            console.log('[getOAuthAuthorize] ✅ 从 localStorage 读取到 oauth_context:', oauthContext);

            // 构建 URL 参数
            const params = new URLSearchParams();
            if (oauthContext.client_id) {
                params.append('client_id', oauthContext.client_id);
            }
            if (oauthContext.redirect_uri) {
                params.append('redirect_uri', oauthContext.redirect_uri);
            }
            if (oauthContext.device_id) {
                params.append('device_id', oauthContext.device_id);
            }
            if (oauthContext.login_trace_id) {
                params.append('login_trace_id', oauthContext.login_trace_id);
            }

            const url = `/api/oauth/authorize?${params.toString()}`;
            console.log('[getOAuthAuthorize] 🚀 准备发送 OAuth 授权请求:', url);

            // 获取 access token 和 workspace_id
            const sessionData = await supabase.auth.getSession();
            const accessToken = sessionData.data.session?.access_token;
            console.log('[getOAuthAuthorize] access_token:', accessToken ? '存在' : '不存在');
            console.log('[getOAuthAuthorize] workspace_id:', workspaceId);
            console.log('[getOAuthAuthorize] login_trace_id:', oauthContext.login_trace_id);

            // 使用 axios 直接请求，设置 maxRedirects: 0 阻止自动跟随重定向
            const response = await axios.get(`${config.services.auth.apiHost}${url}`, {
                maxRedirects: 0, // 不自动跟随重定向
                validateStatus: (status) => status >= 200 && status < 400, // 接受 2xx 和 3xx 状态码
                headers: {
                    'Content-Type': 'application/json',
                    ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
                    ...(workspaceId && { 'X-Workspace-Id': workspaceId }),
                    ...(oauthContext.login_trace_id && { 'X-Login-Trace-Id': oauthContext.login_trace_id }),
                }
            });

            console.log('[getOAuthAuthorize] 响应状态:', response.status);
            console.log('[getOAuthAuthorize] 响应头 location:', response.headers['location'] || response.headers['Location']);

            // 从响应头中获取重定向地址
            const redirectUrl = response.headers['location'] || response.headers['Location'];

            // 删除 bustly_oauth_context
            removeLocalStorageItem('bustly_oauth_context');
            console.log('[getOAuthAuthorize] 🗑️ 已删除 localStorage 中的 bustly_oauth_context');

            if (redirectUrl) {
                console.log('[getOAuthAuthorize] ✅ 跳转到授权地址:', redirectUrl);
                // 使用浏览器导航跳转
                window.location.href = redirectUrl;
            } else {
                console.warn('[getOAuthAuthorize] ⚠️ 未找到重定向地址');
            }
        } catch (error: any) {
            console.error('[getOAuthAuthorize] ❌ 获取 OAuth 授权码失败:', error);
            // 即使失败也删除 bustly_oauth_context
            removeLocalStorageItem('bustly_oauth_context');
            throw error;
        }
    }

}

// 创建 AuthService 实例
export const authService = new AuthService({
    baseURL: config.services.auth.apiHost,
    headers: {'Content-Type': 'application/json'}
});
